import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Pause, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * /human-first — "The Common Thread" cinematic montage.
 *
 * Six anonymous moments, each cross-fading every 7 seconds. Ken-Burns
 * slow zoom on each image; principle word + universal quote overlay.
 * Educational tone; CTA at the end → /the-approach (QMG Blueprint deep-dive).
 *
 * All imagery is royalty-free (Pexels) and intentionally anonymous —
 * silhouettes / landscapes / hands — no recognisable champions.
 */

const MOMENTS = [
  {
    id: 'pressure',
    principle: 'Pressure',
    title: 'They felt what you feel.',
    quote:
      'Every champion on every tee box has felt their hands shake, their breath shorten, their mind race. The pressure is real for all of them.',
    image:
      'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&w=1920&q=80',
    accent: '#BC562B',
  },
  {
    id: 'awareness',
    principle: 'Awareness',
    title: 'They noticed it first.',
    quote:
      'A multiple-major winner once said: "I just kept naming what was happening — my breath, my grip, my hurry." Awareness is the first human skill.',
    image:
      'https://images.unsplash.com/photo-1605144884374-ecbb643615f6?auto=format&fit=crop&w=1920&q=80',
    accent: '#2C5530',
  },
  {
    id: 'acceptance',
    principle: 'Acceptance',
    title: 'They let the feeling stay.',
    quote:
      'They didn\'t fight the nerves. They didn\'t need them to disappear. They accepted the storm — and made their swing anyway.',
    image:
      'https://images.unsplash.com/photo-1532508583690-538a1436f423?auto=format&fit=crop&w=1920&q=80',
    accent: '#BC562B',
  },
  {
    id: 'action',
    principle: 'Action',
    title: 'Commitment, not certainty.',
    quote:
      'One shot. Full belief. Then the next. They committed to the target, not the outcome — freedom, not fear.',
    image:
      'https://images.unsplash.com/photo-1611374243147-44a702c2d44c?auto=format&fit=crop&w=1920&q=80',
    accent: '#1A2E1D',
  },
  {
    id: 'belief',
    principle: 'Belief',
    title: 'A human skill. Trained.',
    quote:
      'Belief is not granted by talent. It\'s rehearsed in quiet moments — long before the cameras, long before the crowds.',
    image:
      'https://images.unsplash.com/photo-1592919505780-303950717480?auto=format&fit=crop&w=1920&q=80',
    accent: '#2C5530',
  },
  {
    id: 'common-thread',
    principle: 'The Common Thread',
    title: 'Different swings. One human.',
    quote:
      'Different grips. Different builds. Different stories. The thread that runs through every winner is the same: they mastered their human skills first. The losing ones let the swing, the thoughts and the feelings overshadow that mastery.',
    image:
      'https://images.unsplash.com/photo-1538648759472-7251f7cb2c2f?auto=format&fit=crop&w=1920&q=80',
    accent: '#BC562B',
  },
];

const SLIDE_DURATION_MS = 7000;

const HumanFirstPage = () => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!playing || prefersReducedMotion) return undefined;
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % MOMENTS.length);
    }, SLIDE_DURATION_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, playing, prefersReducedMotion]);

  const goTo = (newIdx) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIdx(((newIdx % MOMENTS.length) + MOMENTS.length) % MOMENTS.length);
  };

  return (
    <div className="min-h-screen bg-[#0B0F0C] text-white" data-testid="human-first-page">
      <Header />

      {/* Montage stage */}
      <section className="relative h-screen min-h-[640px] overflow-hidden" data-testid="montage-stage">
        {/* Layered cross-fading images */}
        {MOMENTS.map((m, i) => (
          <div
            key={m.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-out ${i === idx ? 'opacity-100' : 'opacity-0'}`}
            aria-hidden={i !== idx}
          >
            <img
              src={m.image}
              alt=""
              className={`w-full h-full object-cover ${i === idx ? 'animate-kenburns' : ''}`}
              loading={i === 0 ? 'eager' : 'lazy'}
            />
            {/* Dark cinematic vignette + warm tint from accent */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F0C]/40 via-[#0B0F0C]/55 to-[#0B0F0C]/90" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F0C]/85 via-transparent to-[#0B0F0C]/30" />
          </div>
        ))}

        {/* Text overlay */}
        <div className="absolute inset-0 z-10 flex items-end pb-24 md:items-center md:pb-0">
          <div className="container mx-auto px-6 md:px-12 max-w-6xl">
            <div className="max-w-2xl" key={MOMENTS[idx].id}>
              <p
                className="text-[10px] md:text-xs uppercase tracking-[0.35em] mb-4 animate-fade-in-up"
                style={{ color: MOMENTS[idx].accent }}
                data-testid="moment-principle"
              >
                {MOMENTS[idx].principle}
              </p>
              <h1
                className="font-['Playfair_Display'] text-3xl md:text-5xl lg:text-6xl leading-[1.05] font-medium mb-6 animate-fade-in-up animation-delay-100 !text-white"
                data-testid="moment-title"
              >
                {MOMENTS[idx].title}
              </h1>
              <p
                className="text-base md:text-lg text-white/85 leading-relaxed max-w-xl animate-fade-in-up animation-delay-200"
                data-testid="moment-quote"
              >
                "{MOMENTS[idx].quote}"
              </p>

              {/* CTA only on final moment */}
              {idx === MOMENTS.length - 1 && (
                <div className="mt-8 animate-fade-in-up animation-delay-300">
                  <Link
                    to="/the-approach"
                    className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-7 py-3.5 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#9A4520] transition-colors"
                    data-testid="moment-cta"
                  >
                    Train the Blueprint <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Controls (bottom centre) */}
        <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-4" data-testid="montage-controls">
          <button
            onClick={() => goTo(idx - 1)}
            className="w-10 h-10 inline-flex items-center justify-center bg-white/5 border border-white/20 hover:bg-white/10 text-white/80 hover:text-white transition-colors"
            aria-label="Previous moment"
            data-testid="montage-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {/* Dots */}
          <div className="flex items-center gap-2">
            {MOMENTS.map((m, i) => (
              <button
                key={m.id}
                onClick={() => goTo(i)}
                className={`h-1 transition-all duration-300 ${i === idx ? 'w-10 bg-[#BC562B]' : 'w-5 bg-white/30 hover:bg-white/60'}`}
                aria-label={`Go to moment ${i + 1}: ${m.principle}`}
                data-testid={`montage-dot-${m.id}`}
              />
            ))}
          </div>

          <button
            onClick={() => setPlaying((p) => !p)}
            className="w-10 h-10 inline-flex items-center justify-center bg-white/5 border border-white/20 hover:bg-white/10 text-white/80 hover:text-white transition-colors"
            aria-label={playing ? 'Pause montage' : 'Play montage'}
            data-testid="montage-play-toggle"
          >
            {playing && !prefersReducedMotion ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>

          <button
            onClick={() => goTo(idx + 1)}
            className="w-10 h-10 inline-flex items-center justify-center bg-white/5 border border-white/20 hover:bg-white/10 text-white/80 hover:text-white transition-colors"
            aria-label="Next moment"
            data-testid="montage-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar (slim, top) */}
        <div className="absolute top-20 left-0 right-0 h-[2px] bg-white/5 z-10">
          <div
            key={`progress-${idx}-${playing}`}
            className={`h-full bg-[#BC562B]/80 ${playing && !prefersReducedMotion ? 'animate-progress' : ''}`}
            style={{ animationDuration: `${SLIDE_DURATION_MS}ms` }}
          />
        </div>
      </section>

      {/* The thesis — calm reading section below the montage */}
      <section className="bg-[#0B0F0C] py-20 md:py-28" data-testid="thesis-section">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.35em] mb-6">Human first. Swing second.</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl leading-tight mb-8 !text-white">
            The winners and the rest weren't separated by talent.
          </h2>
          <div className="text-white/80 text-base md:text-lg leading-[1.85] space-y-5">
            <p>
              Watch any tournament closely. The swings look nothing alike. The bodies look nothing alike.
              The personalities couldn't be more different.
            </p>
            <p>
              And yet the common thread between the winners is almost embarrassing in its simplicity:
              <span className="text-white"> they mastered their human skills first.</span>
            </p>
            <p>
              They noticed the pressure (<span className="text-[#7BB37F] font-semibold">Awareness</span>).
              They accepted what they felt without fighting it (<span className="text-[#E89B6E] font-semibold">Acceptance</span>).
              And they committed fully to the shot in front of them (<span className="text-[#7BB37F] font-semibold">Action</span>).
            </p>
            <p>
              The players who fell short on the day weren't worse golfers. They were great golfers
              who, for a few minutes, let the swing, the thoughts and the feelings overshadow the
              human underneath. Freedom and belief were the casualties.
            </p>
            <p className="pt-2 text-white">
              That's what we train at Quiet Mind Golf. Not a new swing. A trained human.
            </p>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/the-approach"
              className="inline-flex items-center justify-center gap-2 bg-[#BC562B] text-white px-7 py-3.5 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#9A4520] transition-colors"
              data-testid="thesis-cta-primary"
            >
              Explore the QMG Blueprint <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white/85 hover:text-white hover:border-white px-7 py-3.5 text-sm uppercase tracking-[0.2em] font-semibold transition-colors"
              data-testid="thesis-cta-secondary"
            >
              Meet Scott Ritchie
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Local animation rules — slow Ken-Burns + slim progress bar */}
      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1.05) translate3d(0, 0, 0); }
          100% { transform: scale(1.16) translate3d(-1.5%, -1.5%, 0); }
        }
        .animate-kenburns {
          animation: kenburns ${SLIDE_DURATION_MS}ms ease-out forwards;
        }
        @keyframes progress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        .animate-progress {
          animation: progress linear forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-kenburns, .animate-progress { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default HumanFirstPage;
